import { useReduxState } from 'hooks';
import {
  getMessageWithPlaceholders,
  localeLanguageCodes
} from 'shared-locales';
import { breakpoints } from 'styles';
import {
  Avatar,
  Badge,
  EmptyIndicator,
  MoreOptions,
  ProductLinkWithIcon
} from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { CourseUserDeadline } from 'components/courses/common';
import {
  PathStepItemWarning,
  PathStepParticipantsEdit,
  PathStepProgressStatus
} from '.';

import type {
  CourseProgress,
  Deadline,
  PathStep,
  PathStepProgress,
  ResolvedItemInPathStep,
  UserWithBasicData
} from 'shared-types';

type CommonProps = {
  deadlines: { [key: number]: string };
  isUserPage?: boolean;
  loading: boolean;
  progressData: PathStepProgress;
  showAdminOptions?: boolean;
  steps: (Pick<
    PathStep,
    | 'id'
    | 'deadlineDate'
    | 'deadlineDenyAccess'
    | 'deadlineDurationUnit'
    | 'deadlineDurationValue'
    | 'deadlineType'
    | 'isMandatory'
    | 'number'
  > & {
    items: ResolvedItemInPathStep[];
  })[];
  targetUser: UserWithBasicData;
  updateData?: () => Promise<void>;
  userSettings: {
    [key: number]: Pick<
      Deadline,
      | 'deadlineDate'
      | 'deadlineDurationUnit'
      | 'deadlineDurationValue'
      | 'deadlineType'
    >;
  };
};

type OptionalProps =
  | { showAdminOptions: true; updateData: () => Promise<void> }
  | { showAdminOptions?: false };

const PathParticipantSteps = ({
  deadlines,
  steps,
  progressData,
  isUserPage,
  loading,
  userSettings,
  showAdminOptions,
  targetUser,
  updateData
}: CommonProps & OptionalProps) => {
  const { language, messages, theme } = useReduxState();

  return (
    <div className="steps">
      <Box loading={loading} padding={0} title={messages.pathsSteps}>
        {steps && steps.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.pathsStep}</BoxTableCell>
              <BoxTableCell>{messages.commonMandatory}</BoxTableCell>
              <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
              <BoxTableCell>{messages.commonProgress}</BoxTableCell>
              {showAdminOptions ? <BoxTableCell></BoxTableCell> : null}
            </BoxTableHeader>

            {steps.map((step) => {
              const stepDeadline = deadlines[step.id];
              const stepProgress = progressData.steps[step.id];
              const stepUserSettings = userSettings[step.id];

              const verticalAlign = 'top';

              return (
                <BoxTableRow key={step.id}>
                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    verticalAlign={verticalAlign}
                  >
                    <div className="step">
                      <div className="number">
                        <Avatar
                          backgroundColor={theme.lightGreyDark}
                          fontSize="1.4rem"
                        >
                          {step.number}
                        </Avatar>
                      </div>

                      <div className="step-content">
                        <div className="title">
                          {step.items.length > 1 ? (
                            <div className="alternatives">
                              <div className="alternatives-icon">
                                <Icon icon="share-alt" />
                              </div>
                              {getMessageWithPlaceholders({
                                message: messages.commonNumberOfAlternatives,
                                placeholders: {
                                  numberOfAlternatives: step.items.length
                                }
                              })}
                            </div>
                          ) : step.items?.[0] ? (
                            <div className="title-content">
                              <ProductLinkWithIcon
                                preferredLanguageVersion={
                                  step.items[0].type === 'course' && !isUserPage
                                    ? (
                                        stepProgress?.itemProgressByTypeId[
                                          `course${step.items[0].id}`
                                        ] as CourseProgress
                                      )?.completion?.courseLanguageVersion ||
                                      localeLanguageCodes[language]
                                    : undefined
                                }
                                linkToAdminPage={isUserPage}
                                titleStyle={{ fontWeight: 600 }}
                                {...(step.items[0].type === 'achievement'
                                  ? { achievement: step.items[0].achievement }
                                  : step.items[0].type === 'course'
                                  ? { course: step.items[0].course }
                                  : step.items[0].type === 'event'
                                  ? { calendarEvent: step.items[0].event }
                                  : {})}
                              />

                              {step.items[0].type === 'course' ? (
                                !step.items[0].course?.startDate ? (
                                  <PathStepItemWarning
                                    type="unpublished-course"
                                    style={{ top: 4 }}
                                  />
                                ) : step.items[0].course?.userAccess ===
                                  'guests' ? (
                                  <PathStepItemWarning
                                    type="guests-only-course"
                                    style={{ top: 4 }}
                                  />
                                ) : null
                              ) : null}
                            </div>
                          ) : (
                            <div className="step-empty">
                              {messages.commonNoContent}
                            </div>
                          )}
                        </div>

                        {step.items.length > 1 ? (
                          <div className="step-items">
                            {step.items.map((item) => (
                              <div
                                className="step-item"
                                key={`${step.id}-${item.id}`}
                              >
                                <ProductLinkWithIcon
                                  linkToAdminPage={isUserPage}
                                  preferredLanguageVersion={
                                    item.type === 'course' && !isUserPage
                                      ? (
                                          stepProgress?.itemProgressByTypeId[
                                            `course${item.id}`
                                          ] as CourseProgress
                                        )?.completion?.courseLanguageVersion ||
                                        localeLanguageCodes[language]
                                      : undefined
                                  }
                                  titleStyle={{ fontWeight: 600 }}
                                  {...(item.type === 'achievement'
                                    ? { achievement: item.achievement }
                                    : item.type === 'course'
                                    ? { course: item.course }
                                    : item.type === 'event'
                                    ? { calendarEvent: item.event }
                                    : {})}
                                />

                                {item.type === 'course' ? (
                                  !item.course?.startDate ? (
                                    <PathStepItemWarning
                                      type="unpublished-course"
                                      style={{ top: 2 }}
                                    />
                                  ) : item.course?.userAccess === 'guests' ? (
                                    <PathStepItemWarning
                                      type="guests-only-course"
                                      style={{ top: 2 }}
                                    />
                                  ) : null
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </BoxTableCell>

                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    label={messages.commonMandatory}
                    verticalAlign={verticalAlign}
                  >
                    <div className="mandatory">
                      <Badge
                        size="small"
                        text={
                          step.isMandatory
                            ? messages.commonMandatory
                            : messages.commonOptional
                        }
                      />
                    </div>
                  </BoxTableCell>

                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    label={messages.courseTargetSchedule}
                    verticalAlign={verticalAlign}
                  >
                    <div className="deadline">
                      <CourseUserDeadline
                        deadline={stepDeadline}
                        isEdited={
                          showAdminOptions &&
                          Boolean(stepUserSettings?.deadlineType)
                        }
                        noneMessage={''}
                      />
                    </div>
                  </BoxTableCell>

                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    label={messages.commonProgress}
                    verticalAlign={verticalAlign}
                  >
                    <div className="step-progress">
                      <PathStepProgressStatus
                        id={`step-progress-status-${step.id}-${targetUser.id}`}
                        items={step.items}
                        pathCompletion={progressData.pathCompletion}
                        progressData={stepProgress}
                        progressDataByItem={stepProgress.itemProgressByTypeId}
                      />
                    </div>
                  </BoxTableCell>

                  {showAdminOptions ? (
                    <BoxTableCell verticalAlign={verticalAlign} shrink={true}>
                      <MoreOptions>
                        <PathStepParticipantsEdit
                          step={step}
                          targetUser={targetUser}
                          updateUsers={updateData}
                          userSettings={stepUserSettings}
                        />
                      </MoreOptions>
                    </BoxTableCell>
                  ) : null}
                </BoxTableRow>
              );
            })}
          </BoxTable>
        ) : (
          <EmptyIndicator
            description={messages.pathsStepsEmptyDescription}
            icon="book"
            title={messages.pathsStepsEmpty}
          />
        )}
      </Box>

      <style jsx>{`
        .step {
          align-items: center;
          display: flex;
        }
        .step-empty {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-style: italic;
        }
        .number {
          align-self: flex-start;
          margin-right: 20px;
        }
        .step-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .mandatory {
          margin-top: 3px;
        }
        .step-items {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 10px;
        }
        .step-item {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .alternatives {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 4px;
        }
        .alternatives-icon {
          display: inline-block;
          margin-right: 8px;
          text-align: center;
          width: 24px;
        }
        .title-content {
          display: flex;
          gap: 7px;
        }
        .deadline {
          margin-top: 4px;
        }
        .step-progress {
          margin-top: 3px;
        }
        .published {
          margin-right: 20px;
        }
        .warning {
          margin-top: 5px;
          width: 100%;
        }
        .no-completion {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
        }
      `}</style>
    </div>
  );
};

export default PathParticipantSteps;
